import React, { useState, useEffect } from 'react';
import { X, Send, Mail, Upload, FileText, CheckCircle, Save } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import QuickApplicationForm from './QuickApplicationForm';
import VoiceRecorder from './seeker/VoiceRecorder';
import { createJobApplication as createSupabaseApplication, uploadApplicationVoiceRecording, uploadApplicationVideo } from '../../lib/supabase/applications';
import { submitJobApplication as submitFirebaseApplication } from '../../lib/firebase/applications';
import { getJobSeekerProfiles as getSupabaseProfiles } from '../../lib/supabase/jobSeekers';
import { getJobSeekerProfiles as getFirebaseProfiles } from '../../lib/firebase/jobSeekers';
import { getStoredResume } from '../../lib/supabase/subscriptions';
import { useCoverLetter } from '../../hooks/useCoverLetter';
import Button from '../common/Button';
import type { Job } from '../../types/jobs';

interface ApplyJobModalProps {
  job: Job;
  onClose: () => void;
}

export default function ApplyJobModal({ job, onClose }: ApplyJobModalProps) {
  const { user } = useAuth();
  const { activeCoverLetter, saveOrUpdate: saveCoverLetter } = useCoverLetter();
  const [name, setName] = useState(user?.user_metadata?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.user_metadata?.phone || '');
  const [message, setMessage] = useState('');
  const [resume, setResume] = useState<File | null>(null);
  const [storedResumeUrl, setStoredResumeUrl] = useState<string | null>(null);
  const [useStoredResume, setUseStoredResume] = useState(false);
  const [voiceIntroUrl, setVoiceIntroUrl] = useState<string | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [isUploadingVoice, setIsUploadingVoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [voiceUploadSuccess, setVoiceUploadSuccess] = useState(false);
  const [coverLetterSaved, setCoverLetterSaved] = useState(false);

  useEffect(() => {
    async function loadStoredResume() {
      if (user?.id) {
        try {
          const resumeUrl = await getStoredResume(user.id);
          if (resumeUrl) {
            setStoredResumeUrl(resumeUrl);
            setUseStoredResume(true);
          }
        } catch (err) {
          console.error('Error loading stored resume:', err);
        }
      }
    }
    loadStoredResume();
  }, [user]);

  useEffect(() => {
    if (activeCoverLetter && !message) {
      setMessage(activeCoverLetter.content);
    }
  }, [activeCoverLetter]);

  if (!user) {
    return <QuickApplicationForm job={job} onClose={onClose} />;
  }

  const handleEmailCapture = (e: React.FormEvent) => {
    e.preventDefault();

    const applicationData = {
      jobId: job.id,
      jobTitle: job.title,
      farmerName: job.farmerName,
      email: email,
      name: name,
      phone: phone,
      timestamp: Date.now()
    };
    localStorage.setItem('pendingJobApplication', JSON.stringify(applicationData));

    window.location.href = `/signup?email=${encodeURIComponent(email)}&job=${encodeURIComponent(job.title)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      setError('Please sign in to apply');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (message.trim()) {
        await saveCoverLetter(message.trim());
        setCoverLetterSaved(true);
      }

      let applicationError = null;

      const isSupabaseJob = job.id.includes('-') && job.id.length > 20;

      if (isSupabaseJob) {
        const resumeToUse = useStoredResume && storedResumeUrl ? storedResumeUrl : undefined;

        const result = await createSupabaseApplication({
          jobId: job.id,
          message: `Name: ${name}\nEmail: ${email}\nPhone: ${phone}\n\nMessage:\n${message.trim()}`,
          resumeFile: resume || undefined,
          existingResumeUrl: resumeToUse,
          voiceIntroUrl: voiceIntroUrl || undefined,
          videoFile: videoFile || undefined
        });
        applicationError = result.error;
      } else {
        const result = await submitFirebaseApplication({
          jobId: job.id,
          farmhandId: user.id,
          message: message.trim(),
          status: 'pending',
          createdAt: new Date().toISOString()
        });
        applicationError = result.error;
      }

      if (applicationError) {
        throw applicationError;
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error submitting application:', err);
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg max-w-sm w-full p-5 text-center">
          <div className="bg-green-100 rounded-full p-2.5 w-14 h-14 mx-auto mb-3 flex items-center justify-center">
            <Send className="h-7 w-7 text-green-600" />
          </div>

          <h2 className="text-xl font-semibold mb-1.5">Application Submitted!</h2>
          <p className="text-sm text-gray-600">
            Your application for <strong>{job.title}</strong> has been sent successfully!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-md mx-auto p-4 sm:p-6 relative my-2 sm:my-4 max-h-[98vh] sm:max-h-[95vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 z-10"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="mb-4 sm:mb-5">
          <h2 className="text-lg sm:text-xl font-semibold mb-1">Apply for Position</h2>
          <p className="text-xs sm:text-sm text-gray-600">
            Applying for <strong>{job.title}</strong>
          </p>
          <div className="mt-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3">
            <p className="text-xs sm:text-sm font-medium text-green-900 mb-1.5">
              Save time on future applications!
            </p>
            <p className="text-xs text-green-800 leading-relaxed">
              Create an account to save your resume and cover letter. Apply to jobs in seconds without re-entering the same information again and again.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-2.5 text-red-800 text-xs">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1">
              Full Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              placeholder="Enter your full name"
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              placeholder="(555) 123-4567"
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="resume" className="block text-xs font-medium text-gray-700">
                Resume (Optional)
              </label>
              {storedResumeUrl && useStoredResume && !resume && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Auto-filled
                </span>
              )}
            </div>

            {storedResumeUrl && useStoredResume && !resume ? (
              <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-green-900">Using saved resume</p>
                      <p className="text-xs text-green-700 truncate">{storedResumeUrl.split('/').pop()?.replace(/^\d+-/, '') || 'resume.pdf'}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setUseStoredResume(false)}
                    className="text-xs text-green-700 hover:text-green-900 font-medium underline flex-shrink-0"
                  >
                    Change
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1">
                <input
                  id="resume"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setResume(file);
                    if (file) {
                      setUseStoredResume(false);
                    }
                  }}
                  className="hidden"
                />
                <label
                  htmlFor="resume"
                  className="flex items-center justify-center w-full px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  {resume ? (
                    <div className="flex items-center space-x-1.5 text-green-600 max-w-full">
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <span className="text-xs font-medium truncate">{resume.name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1.5 text-gray-500">
                      <Upload className="h-4 w-4 flex-shrink-0" />
                      <span className="text-xs">Upload resume (PDF, DOC, DOCX)</span>
                    </div>
                  )}
                </label>
                {resume && (
                  <p className="mt-1.5 text-xs text-gray-600">
                    {storedResumeUrl
                      ? 'This will replace your saved resume for future applications.'
                      : 'This will be saved for future applications.'}
                  </p>
                )}
                {!resume && storedResumeUrl && !useStoredResume && (
                  <button
                    type="button"
                    onClick={() => setUseStoredResume(true)}
                    className="mt-1.5 text-xs text-green-600 hover:text-green-700 font-medium"
                  >
                    Use saved resume instead
                  </button>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="message" className="block text-xs font-medium text-gray-700">
                Cover Letter / Description
              </label>
              {activeCoverLetter && (
                <span className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Auto-filled
                </span>
              )}
            </div>
            <textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-md border border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 px-3 py-2 text-sm sm:text-base"
              rows={4}
              placeholder="Tell the employer why you're interested in this position and highlight your relevant experience."
              required
            />
            {message && message !== activeCoverLetter?.content && (
              <div className="mt-1.5 flex items-center justify-between">
                <p className="text-xs text-gray-600">
                  {activeCoverLetter ? 'Modified from saved version' : 'This will be saved for future applications'}
                </p>
                <button
                  type="button"
                  onClick={async () => {
                    if (message.trim()) {
                      await saveCoverLetter(message.trim());
                      setCoverLetterSaved(true);
                      setTimeout(() => setCoverLetterSaved(false), 3000);
                    }
                  }}
                  className="text-xs text-green-600 hover:text-green-700 font-medium flex items-center gap-1"
                >
                  <Save className="h-3 w-3" />
                  Save Now
                </button>
              </div>
            )}
            {coverLetterSaved && (
              <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3 w-3" />
                Cover letter saved for future use!
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Video Introduction <span className="text-gray-500">(Optional)</span>
            </label>
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  const maxSize = 50 * 1024 * 1024;
                  if (file.size > maxSize) {
                    setError('Video file must be less than 50MB');
                    e.target.value = '';
                    return;
                  }
                  setVideoFile(file);
                  setError(null);
                }
              }}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Upload a short video introduction (max 1 minute, 50MB max)
            </p>
            {videoFile && (
              <p className="mt-1 text-xs text-green-600">
                Selected: {videoFile.name} ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Voice Introduction <span className="text-gray-500">(Optional)</span>
            </label>
            {voiceIntroUrl ? (
              <div className="space-y-1.5">
                <audio controls className="w-full" style={{ height: '32px' }}>
                  <source src={voiceIntroUrl} type="audio/webm" />
                  Your browser does not support the audio element.
                </audio>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVoiceIntroUrl(null)}
                    className="text-xs"
                  >
                    Record New
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(voiceIntroUrl);
                      setVoiceUploadSuccess(true);
                      setTimeout(() => setVoiceUploadSuccess(false), 2000);
                    }}
                    className="text-xs"
                  >
                    🔗 Copy Link
                  </Button>
                </div>
                {voiceUploadSuccess && (
                  <p className="text-xs text-green-600">✓ Link copied to clipboard!</p>
                )}
              </div>
            ) : (
              <div className="space-y-1.5">
                <VoiceRecorder
                  maxDuration={60}
                  disabled={isUploadingVoice || loading}
                  onSave={async (audioBlob) => {
                    if (!user?.id) throw new Error('User not authenticated');

                    if (isUploadingVoice) {
                      throw new Error('Upload already in progress');
                    }

                    setIsUploadingVoice(true);
                    setError(null);

                    try {
                      const tempId = `temp-${Date.now()}-${user.id.slice(0, 8)}`;
                      const { url, error: uploadError } = await uploadApplicationVoiceRecording(audioBlob, tempId);

                      if (uploadError) {
                        throw uploadError;
                      }

                      if (!url) {
                        throw new Error('Failed to generate public URL');
                      }

                      setVoiceIntroUrl(url);
                      setVoiceUploadSuccess(true);
                      setTimeout(() => setVoiceUploadSuccess(false), 5000);
                    } finally {
                      setIsUploadingVoice(false);
                    }
                  }}
                />
                {voiceUploadSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-2 text-xs text-green-800">
                    ✓ Voice recording uploaded successfully! Public URL has been generated.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="pt-2">
            <Button
              type="submit"
              variant="primary"
              icon={Send}
              isLoading={loading}
              disabled={loading || isUploadingVoice}
              fullWidth
              size="lg"
              className="text-base py-3"
            >
              {isUploadingVoice ? 'Uploading Voice...' : 'Submit Application'}
            </Button>
          </div>

          <div className="text-center space-y-2">
            <p className="text-xs text-gray-500">
              Your application will be sent directly to the employer.
            </p>
            <p className="text-xs font-medium text-green-700">
              Your resume and cover letter will be saved for instant future applications.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
